import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, ChevronRight, Clock, Globe, Mail, FileText,
  GripVertical, Trash2, ExternalLink, Activity, Star,
  Users, UserCheck, Handshake, Rocket, CheckCircle, XCircle,
  Filter, RefreshCw, Phone, MessageSquare, Tag, Calendar,
  DollarSign, Send, AlertCircle, Download
} from 'lucide-react';
import EmailPopup from '../emailpop/EmailPopup';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const STAGES = [
  { key: 'prospected', label: 'Prospected', icon: Users, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-200 dark:border-blue-800', headerBg: 'bg-blue-400', badgeBg: 'bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-300' },
  { key: 'contacted', label: 'Contacted', icon: Mail, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-200 dark:border-blue-800', headerBg: 'bg-blue-500', badgeBg: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  { key: 'negotiating', label: 'Negotiating', icon: Handshake, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-300 dark:border-blue-800', headerBg: 'bg-blue-600', badgeBg: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  { key: 'onboarding', label: 'Onboarding', icon: Rocket, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-300 dark:border-blue-700', headerBg: 'bg-blue-700', badgeBg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  { key: 'active', label: 'Active', icon: CheckCircle, color: 'blue', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-400 dark:border-blue-700', headerBg: 'bg-blue-800', badgeBg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { key: 'churned', label: 'Churned', icon: XCircle, color: 'slate', bgColor: 'bg-slate-50 dark:bg-slate-900/20', borderColor: 'border-slate-300 dark:border-slate-700', headerBg: 'bg-slate-500', badgeBg: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
];

const OFFER_TYPE_LABELS = {
  cpa: 'CPA',
  revenue_share: 'Revenue Share',
  hybrid: 'Hybrid',
  flat_fee: 'Flat Fee'
};

const OFFER_STATUS_COLORS = {
  proposed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  countered: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  rejected: 'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400'
};

const CONTACT_ICONS = {
  email: Mail,
  call: Phone,
  meeting: Users,
  message: MessageSquare
};

const CrmPipeline = () => {
  const [pipelineData, setPipelineData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ affiliate_url: '', affiliate_name: '', email: '' });
  const [toast, setToast] = useState(null);
  const [filterSource, setFilterSource] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);

  // Detail panel tabs & data
  const [activeTab, setActiveTab] = useState('overview');
  const [notes, setNotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newOffer, setNewOffer] = useState({ offer_type: 'cpa', offer_value: '', status: 'proposed', note: '' });
  const [newContact, setNewContact] = useState({ contact_type: 'email', subject: '', notes: '' });
  const [cardPriority, setCardPriority] = useState('medium');
  const [cardFollowUp, setCardFollowUp] = useState('');
  const [cardTags, setCardTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [savingField, setSavingField] = useState(null);

  // Compose-email popup (per-card "Contact" button)
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [emailPopupCard, setEmailPopupCard] = useState(null);
  const [emailPopupRecipient, setEmailPopupRecipient] = useState(null);
  const [emailPopupAllEmails, setEmailPopupAllEmails] = useState([]);
  const [emailPopupSocialMedia, setEmailPopupSocialMedia] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch user brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(`${basePath}/api/crm/brands`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (data.success && data.data.length > 0) {
          setBrands(data.data);
          setSelectedBrandId(data.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setIsLoadingBrands(false);
      }
    };
    fetchBrands();
  }, []);

  const fetchPipelineData = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      const response = await fetch(`${basePath}/api/crm?brand_id=${selectedBrandId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setPipelineData(data.data);
      }
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBrandId]);

  useEffect(() => {
    if (selectedBrandId) {
      setIsLoading(true);
      fetchPipelineData();
    }
  }, [fetchPipelineData, selectedBrandId]);

  const fetchActivityLog = async (pipelineId) => {
    try {
      const response = await fetch(`${basePath}/api/crm/activity/${pipelineId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setActivityLog(data.data);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    }
  };

  const fetchNotes = async (pipelineId) => {
    try {
      const response = await fetch(`${basePath}/api/crm/notes/${pipelineId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setNotes(data.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchOffers = async (pipelineId) => {
    try {
      const response = await fetch(`${basePath}/api/crm/offers/${pipelineId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setOffers(data.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const fetchContacts = async (pipelineId) => {
    try {
      const response = await fetch(`${basePath}/api/crm/contacts/${pipelineId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setContacts(data.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // --- Handlers ---

  const handleStageChange = async (itemId, newStage) => {
    try {
      const response = await fetch(`${basePath}/api/crm/stage`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemId, stage: newStage })
      });
      const data = await response.json();
      if (data.success) {
        fetchPipelineData();
        if (selectedCard && selectedCard.id === itemId) {
          setSelectedCard({ ...selectedCard, stage: newStage });
          fetchActivityLog(itemId);
        }
        showToast(`Moved to ${newStage}`);
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      showToast('Failed to update stage', 'error');
    }
  };

  const handleUpdateField = async (fieldUpdates) => {
    if (!selectedCard) return;
    setSavingField(Object.keys(fieldUpdates)[0]);
    try {
      const response = await fetch(`${basePath}/api/crm/update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedCard.id, ...fieldUpdates })
      });
      const data = await response.json();
      if (data.success) {
        fetchPipelineData();
        showToast('Updated');
      }
    } catch (error) {
      console.error('Error updating:', error);
      showToast('Failed to update', 'error');
    } finally {
      setSavingField(null);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedCard) return;
    try {
      const response = await fetch(`${basePath}/api/crm/notes/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pipeline_id: selectedCard.id, note_text: newNote.trim() })
      });
      const data = await response.json();
      if (data.success) {
        setNewNote('');
        fetchNotes(selectedCard.id);
        fetchActivityLog(selectedCard.id);
        showToast('Note added');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showToast('Failed to add note', 'error');
    }
  };

  const handleAddOffer = async () => {
    if (!newOffer.offer_value || !selectedCard) return;
    try {
      const response = await fetch(`${basePath}/api/crm/offers/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pipeline_id: selectedCard.id,
          offer_type: newOffer.offer_type,
          offer_value: newOffer.offer_value,
          status: newOffer.status,
          note: newOffer.note || null
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewOffer({ offer_type: 'cpa', offer_value: '', status: 'proposed', note: '' });
        fetchOffers(selectedCard.id);
        fetchActivityLog(selectedCard.id);
        fetchPipelineData();
        showToast('Offer added');
      }
    } catch (error) {
      console.error('Error adding offer:', error);
      showToast('Failed to add offer', 'error');
    }
  };

  const handleUpdateOffer = async (offerId, status) => {
    try {
      const response = await fetch(`${basePath}/api/crm/offers/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer_id: offerId, status })
      });
      const data = await response.json();
      if (data.success) {
        fetchOffers(selectedCard.id);
        fetchActivityLog(selectedCard.id);
        fetchPipelineData();
        showToast(`Offer ${status}`);
      }
    } catch (error) {
      console.error('Error updating offer:', error);
      showToast('Failed to update offer', 'error');
    }
  };

  const handleAddContact = async () => {
    if (!selectedCard) return;
    try {
      const response = await fetch(`${basePath}/api/crm/contacts/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pipeline_id: selectedCard.id,
          contact_type: newContact.contact_type,
          subject: newContact.subject || null,
          notes: newContact.notes || null
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewContact({ contact_type: 'email', subject: '', notes: '' });
        fetchContacts(selectedCard.id);
        fetchActivityLog(selectedCard.id);
        showToast('Contact logged');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      showToast('Failed to log contact', 'error');
    }
  };

  const handleContactClick = async (e, item) => {
    e.stopPropagation();

    // Open popup immediately with whatever email the card already has
    const existing = item.email ? [item.email] : [];
    setEmailPopupCard(item);
    setEmailPopupRecipient({
      email: item.email || '',
      value: item.email || '',
      name: item.affiliate_name || item.affiliate_url,
      url: item.affiliate_url
    });
    setEmailPopupAllEmails(existing);
    setEmailPopupSocialMedia({});
    setEmailPopupOpen(true);

    // Fetch additional contacts in background and append to the dropdown
    try {
      const domain = (item.affiliate_url || '')
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split('/')[0]
        .split('?')[0];
      const response = await fetch(`${basePath}/api/contact-finder`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain })
      });
      const data = await response.json();
      if (response.ok && data) {
        const fetched = (data.contacts || [])
          .map(c => c.value || c.email)
          .filter(Boolean);
        const merged = [...new Set([...existing, ...fetched])];
        setEmailPopupAllEmails(merged);
        setEmailPopupSocialMedia(data.socialMedia || {});
        if (!item.email && merged.length > 0) {
          setEmailPopupRecipient(prev => prev ? { ...prev, email: merged[0], value: merged[0] } : prev);
        }
      }
    } catch (err) {
      // Non-fatal — the popup is already open, user can type the email manually
      console.error('Contact finder error:', err);
    }
  };

  const closeEmailPopup = () => {
    setEmailPopupOpen(false);
    setEmailPopupCard(null);
    setEmailPopupRecipient(null);
    setEmailPopupAllEmails([]);
    setEmailPopupSocialMedia({});
  };

  const handleEmailSendFromCard = async (emailForm) => {
    const response = await fetch(`${basePath}/api/emailsent`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: emailForm.to,
        cc: emailForm.cc || '',
        subject: emailForm.subject,
        message: emailForm.message,
        attachments: emailForm.attachments || [],
        sender_email_id: emailForm.sender_email_id || 'primary'
      })
    });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(errText || `Failed to send email (${response.status})`);
    }
    const result = await response.json().catch(() => ({}));
    if (!result.success) throw new Error(result.error || 'Failed to send email');

    // Log into CRM: save contact activity, persist email, auto-advance stage
    if (emailPopupCard) {
      try {
        await fetch(`${basePath}/api/crm/contacts/add`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pipeline_id: emailPopupCard.id,
            contact_type: 'email',
            subject: emailForm.subject || null,
            notes: `Sent to ${emailForm.to}`
          })
        });

        const firstTo = (emailForm.to || '').split(',')[0].trim();
        if (!emailPopupCard.email && firstTo) {
          await fetch(`${basePath}/api/crm/update`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: emailPopupCard.id, email: firstTo })
          });
        }

        if (emailPopupCard.stage === 'prospected') {
          await fetch(`${basePath}/api/crm/stage`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: emailPopupCard.id, stage: 'contacted' })
          });
        }

        fetchPipelineData();
      } catch (err) {
        console.error('Error logging CRM activity after send:', err);
      }
    }

    showToast('Email sent successfully');
  };

  const handlePriorityChange = (priority) => {
    setCardPriority(priority);
    handleUpdateField({ priority });
  };

  const handleFollowUpChange = (date) => {
    setCardFollowUp(date);
    handleUpdateField({ follow_up_date: date || null });
  };

  const handleAddTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (!tag || cardTags.includes(tag)) { setNewTag(''); return; }
    const updated = [...cardTags, tag];
    setCardTags(updated);
    setNewTag('');
    handleUpdateField({ tags: updated });
  };

  const handleRemoveTag = (tag) => {
    const updated = cardTags.filter(t => t !== tag);
    setCardTags(updated);
    handleUpdateField({ tags: updated });
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Remove this affiliate from the pipeline?')) return;
    try {
      const response = await fetch(`${basePath}/api/crm/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setShowDetailPanel(false);
        setSelectedCard(null);
        fetchPipelineData();
        showToast('Removed from pipeline');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showToast('Failed to delete', 'error');
    }
  };

  const handleAddManual = async () => {
    if (!addForm.affiliate_url) {
      showToast('Affiliate URL is required', 'error');
      return;
    }
    if (!selectedBrandId) {
      showToast('Please select a brand first', 'error');
      return;
    }
    try {
      const response = await fetch(`${basePath}/api/crm/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...addForm, source: 'manual', brand_id: selectedBrandId })
      });
      const data = await response.json();
      if (data.success) {
        setShowAddModal(false);
        setAddForm({ affiliate_url: '', affiliate_name: '', email: '' });
        fetchPipelineData();
        showToast('Affiliate added to pipeline');
      } else {
        showToast(data.message || 'Failed to add', 'error');
      }
    } catch (error) {
      console.error('Error adding to pipeline:', error);
      showToast('Failed to add', 'error');
    }
  };

  const openDetailPanel = (card) => {
    setSelectedCard(card);
    setActiveTab('overview');
    setCardPriority(card.priority || 'medium');
    setCardFollowUp(card.follow_up_date ? card.follow_up_date.split('T')[0] : '');
    setCardTags(card.tags || []);
    setShowDetailPanel(true);
    setNewNote('');
    setNewOffer({ offer_type: 'cpa', offer_value: '', status: 'proposed', note: '' });
    setNewContact({ contact_type: 'email', subject: '', notes: '' });
    fetchActivityLog(card.id);
    fetchNotes(card.id);
    fetchOffers(card.id);
    fetchContacts(card.id);
  };

  // Drag and drop
  const handleDragStart = (e, item, fromStage) => {
    setDraggedItem({ ...item, fromStage });
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };
  const handleDragLeave = () => setDragOverStage(null);
  const handleDrop = (e, toStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (draggedItem && draggedItem.fromStage !== toStage) {
      handleStageChange(draggedItem.id, toStage);
    }
    setDraggedItem(null);
  };
  const handleDragEnd = () => { setDraggedItem(null); setDragOverStage(null); };

  // Filters
  const getFilteredItems = (stageKey) => {
    const items = pipelineData[stageKey] || [];
    return items.filter(item => {
      const matchesSearch = !searchQuery ||
        item.affiliate_url?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.affiliate_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource = !filterSource || item.source === filterSource;
      const matchesTag = !filterTag || (item.tags && item.tags.includes(filterTag));
      return matchesSearch && matchesSource && matchesTag;
    });
  };

  const allSources = [...new Set(
    Object.values(pipelineData).flat().map(item => item.source).filter(Boolean)
  )];

  const allTags = [...new Set(
    Object.values(pipelineData).flat().flatMap(item => item.tags || [])
  )].sort();

  const totalItems = Object.values(pipelineData).flat().length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Follow-up date helpers
  const getFollowUpStatus = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fDate = new Date(dateStr);
    fDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((fDate - today) / 86400000);
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'today';
    if (diffDays <= 3) return 'upcoming';
    return 'future';
  };

  const followUpDotColor = {
    overdue: 'bg-blue-800',
    today: 'bg-blue-600',
    upcoming: 'bg-blue-400',
    future: 'bg-gray-300 dark:bg-slate-600'
  };

  // Export pipeline data as CSV
  const handleExportCSV = () => {
    const allItems = Object.values(pipelineData).flat();
    if (allItems.length === 0) {
      showToast('No data to export', 'error');
      return;
    }

    const headers = ['Affiliate URL', 'Affiliate Name', 'Email', 'Stage', 'Priority', 'Follow-up Date', 'Tags', 'Commission Type', 'Commission Value', 'Source', 'Brand', 'Created', 'Last Updated'];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = allItems.map(item => [
      escapeCSV(item.affiliate_url),
      escapeCSV(item.affiliate_name),
      escapeCSV(item.email),
      escapeCSV(item.stage),
      escapeCSV(item.priority || 'medium'),
      escapeCSV(item.follow_up_date ? item.follow_up_date.split('T')[0] : ''),
      escapeCSV((item.tags || []).join(', ')),
      escapeCSV(item.commission_type),
      escapeCSV(item.commission_value),
      escapeCSV(item.source),
      escapeCSV(item.brand_name),
      escapeCSV(item.created_at ? new Date(item.created_at).toLocaleDateString() : ''),
      escapeCSV(item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '')
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const brandName = brands.find(b => b.id === selectedBrandId)?.brand_name || 'all';
    link.href = url;
    link.download = `crm-pipeline-${brandName}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${allItems.length} affiliates`);
  };

  if (isLoading && isLoadingBrands) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  // --- Detail Panel Tab Content Renderers ---

  const renderOverviewTab = () => (
    <div className="p-4 space-y-5">
      {/* Stage Selector */}
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Current Stage</label>
        <div className="grid grid-cols-3 gap-2">
          {STAGES.map(stage => {
            const StageIcon = stage.icon;
            const isActive = selectedCard.stage === stage.key;
            return (
              <button key={stage.key} onClick={() => handleStageChange(selectedCard.id, stage.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                  isActive ? `${stage.headerBg} text-white shadow-md` : `${stage.bgColor} ${stage.borderColor} border hover:shadow-sm text-gray-700 dark:text-slate-300`
                }`}>
                <StageIcon className="w-3.5 h-3.5" />
                {stage.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Priority</label>
        <div className="flex gap-2">
          {[
            { key: 'high', label: 'High', color: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700', active: 'bg-blue-800 text-white border-blue-800' },
            { key: 'medium', label: 'Medium', color: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800', active: 'bg-blue-600 text-white border-blue-600' },
            { key: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600', active: 'bg-gray-600 text-white border-gray-600' }
          ].map(p => (
            <button key={p.key} onClick={() => handlePriorityChange(p.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${cardPriority === p.key ? p.active : p.color}`}>
              {p.key === 'high' && <Star className="w-3 h-3 inline mr-1" />}
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Follow-up Date */}
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Follow-up Date</label>
        <div className="flex items-center gap-2">
          <input type="date" value={cardFollowUp} onChange={(e) => handleFollowUpChange(e.target.value)}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {cardFollowUp && (
            <>
              <button onClick={() => handleFollowUpChange('')} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                <X className="w-4 h-4" />
              </button>
              {(() => {
                const status = getFollowUpStatus(cardFollowUp);
                const labels = { overdue: 'Overdue', today: 'Today', upcoming: 'Soon', future: '' };
                const colors = { overdue: 'text-blue-800 bg-blue-50 dark:bg-blue-900/20', today: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20', upcoming: 'text-blue-400 bg-blue-50 dark:bg-blue-900/20' };
                return status && labels[status] ? (
                  <span className={`text-xs font-medium px-2 py-1 rounded ${colors[status]}`}>
                    {status === 'overdue' && <AlertCircle className="w-3 h-3 inline mr-0.5" />}
                    {labels[status]}
                  </span>
                ) : null;
              })()}
            </>
          )}
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Tags</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {cardTags.map(tag => (
            <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
              <Tag className="w-3 h-3" />
              {tag}
              <button onClick={() => handleRemoveTag(tag)} className="hover:text-blue-600 ml-0.5"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {cardTags.length === 0 && <span className="text-xs text-gray-400 dark:text-slate-500 italic">No tags</span>}
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="Add tag..." value={newTag} onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-1.5 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button onClick={handleAddTag} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">Add</button>
        </div>
      </div>

      {/* Current Commission (read-only, shows latest accepted offer) */}
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Current Commission</label>
        {selectedCard.commission_type ? (
          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {OFFER_TYPE_LABELS[selectedCard.commission_type] || selectedCard.commission_type} — {selectedCard.commission_value}
            </span>
          </div>
        ) : (
          <p className="text-xs text-gray-400 dark:text-slate-500 italic">No accepted offer yet. Go to Offers tab to add one.</p>
        )}
      </div>

      {/* Source & dates */}
      <div className="border-t border-gray-100 dark:border-slate-700 pt-4 text-xs text-gray-400 dark:text-slate-500 space-y-1">
        {selectedCard.source && <p>Source: {selectedCard.source}</p>}
        <p>Added: {new Date(selectedCard.created_at).toLocaleDateString()}</p>
        <p>Last updated: {formatDate(selectedCard.updated_at)}</p>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="p-4 space-y-4">
      {/* Add note */}
      <div className="space-y-2">
        <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
          placeholder="Write a note..." rows={3}
          className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        <button onClick={handleAddNote} disabled={!newNote.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <Send className="w-3.5 h-3.5" /> Add Note
        </button>
      </div>
      {/* Notes thread */}
      <div className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-6">No notes yet. Add your first note above.</p>
        ) : (
          notes.map(note => (
            <div key={note.id} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700">
              <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">{note.note_text}</p>
              <span className="text-[10px] text-gray-400 dark:text-slate-500 mt-2 block">{formatDate(note.created_at)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderOffersTab = () => (
    <div className="p-4 space-y-4">
      {/* Add offer form */}
      <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 space-y-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">New Offer</p>
        <div className="grid grid-cols-2 gap-2">
          <select value={newOffer.offer_type} onChange={(e) => setNewOffer(p => ({ ...p, offer_type: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="cpa">CPA</option>
            <option value="revenue_share">Revenue Share</option>
            <option value="hybrid">Hybrid</option>
            <option value="flat_fee">Flat Fee</option>
          </select>
          <input type="text" placeholder="Value (e.g. $50, 30%)" value={newOffer.offer_value}
            onChange={(e) => setNewOffer(p => ({ ...p, offer_value: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={newOffer.status} onChange={(e) => setNewOffer(p => ({ ...p, status: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="proposed">Proposed (We offered)</option>
            <option value="countered">Countered (They offered)</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
          <input type="text" placeholder="Note (optional)" value={newOffer.note}
            onChange={(e) => setNewOffer(p => ({ ...p, note: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button onClick={handleAddOffer} disabled={!newOffer.offer_value}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <DollarSign className="w-3.5 h-3.5" /> Add Offer
        </button>
      </div>

      {/* Offer timeline */}
      <div className="space-y-3">
        {offers.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-6">No offers yet.</p>
        ) : (
          offers.map(offer => (
            <div key={offer.id} className={`p-3 rounded-lg border ${offer.status === 'accepted' ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700'}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {OFFER_TYPE_LABELS[offer.offer_type] || offer.offer_type}
                  </span>
                  <span className="text-sm font-bold text-gray-700 dark:text-slate-300">{offer.offer_value}</span>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${OFFER_STATUS_COLORS[offer.status]}`}>
                  {offer.status}
                </span>
              </div>
              {offer.note && <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{offer.note}</p>}
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-gray-400 dark:text-slate-500">{formatDate(offer.created_at)}</span>
                {(offer.status === 'proposed' || offer.status === 'countered') && (
                  <div className="flex gap-1">
                    <button onClick={() => handleUpdateOffer(offer.id, 'accepted')}
                      className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded font-medium hover:bg-blue-200 transition-colors">
                      Accept
                    </button>
                    <button onClick={() => handleUpdateOffer(offer.id, 'rejected')}
                      className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-400 rounded font-medium hover:bg-slate-200 transition-colors">
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderContactsTab = () => (
    <div className="p-4 space-y-4">
      {/* Log contact form */}
      <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 space-y-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Log Contact</p>
        <div className="grid grid-cols-2 gap-2">
          <select value={newContact.contact_type} onChange={(e) => setNewContact(p => ({ ...p, contact_type: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="email">Email</option>
            <option value="call">Call</option>
            <option value="meeting">Meeting</option>
            <option value="message">Message</option>
          </select>
          <input type="text" placeholder="Subject" value={newContact.subject}
            onChange={(e) => setNewContact(p => ({ ...p, subject: e.target.value }))}
            className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <textarea value={newContact.notes} onChange={(e) => setNewContact(p => ({ ...p, notes: e.target.value }))}
          placeholder="Notes about this contact..." rows={2}
          className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        <button onClick={handleAddContact}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Log Contact
        </button>
      </div>

      {/* Contact list */}
      <div className="space-y-3">
        {contacts.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-6">No contacts logged yet.</p>
        ) : (
          contacts.map(contact => {
            const ContactIcon = CONTACT_ICONS[contact.contact_type] || MessageSquare;
            return (
              <div key={contact.id} className="flex gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  contact.contact_type === 'email' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' :
                  contact.contact_type === 'call' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500' :
                  contact.contact_type === 'meeting' ? 'bg-blue-200 dark:bg-blue-900/40 text-blue-700' :
                  'bg-gray-100 dark:bg-slate-700 text-gray-600'
                }`}>
                  <ContactIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-300 uppercase">{contact.contact_type}</span>
                    {contact.subject && <span className="text-sm text-gray-600 dark:text-slate-400 truncate">— {contact.subject}</span>}
                  </div>
                  {contact.notes && <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{contact.notes}</p>}
                  {contact.email_subject && (
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {contact.email_subject} → {contact.recipient_email}
                    </p>
                  )}
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 block">{formatDate(contact.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="p-4">
      <div className="space-y-3">
        {activityLog.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-4">No activity yet</p>
        ) : (
          activityLog.map(activity => (
            <div key={activity.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${
                  activity.activity_type === 'stage_change' ? 'bg-blue-500' :
                  activity.activity_type === 'added' ? 'bg-blue-400' :
                  activity.activity_type === 'offer_added' || activity.activity_type === 'offer_updated' ? 'bg-blue-600' :
                  activity.activity_type === 'contact_logged' ? 'bg-blue-700' :
                  activity.activity_type === 'note_added' ? 'bg-blue-300' : 'bg-gray-400'
                }`} />
                <div className="w-px flex-1 bg-gray-200 dark:bg-slate-700 mt-1" />
              </div>
              <div className="pb-3">
                <p className="text-xs text-gray-700 dark:text-slate-300">{activity.description}</p>
                {activity.old_stage && activity.new_stage && (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">{activity.old_stage}</span>
                    <ChevronRight className="w-3 h-3 text-gray-400" />
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">{activity.new_stage}</span>
                  </div>
                )}
                <span className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 block">{formatDate(activity.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // --- RENDER ---

  return (
    <div className="p-6 max-w-full">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
          toast.type === 'error' ? 'bg-slate-700' : 'bg-blue-600'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Affiliate Pipeline</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Track and manage your affiliate relationships — {totalItems} total affiliates
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Brand:</label>
              {isLoadingBrands ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg">
                  <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-400">Loading...</span>
                </div>
              ) : brands.length === 0 ? (
                <span className="text-sm text-gray-400 dark:text-slate-500">No brands found</span>
              ) : (
                <select value={selectedBrandId || ''} onChange={(e) => setSelectedBrandId(parseInt(e.target.value))}
                  className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]">
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.brand_name || brand.brand_url}</option>
                  ))}
                </select>
              )}
            </div>
            <button onClick={handleExportCSV} disabled={totalItems === 0}
              className="border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => setShowAddModal(true)} disabled={!selectedBrandId}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" /> Add Affiliate
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-6 gap-3 mb-4">
          {STAGES.map(stage => {
            const count = (pipelineData[stage.key] || []).length;
            const StageIcon = stage.icon;
            return (
              <div key={stage.key} className={`${stage.bgColor} ${stage.borderColor} border rounded-lg p-3 text-center`}>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <StageIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{stage.label}</span>
                </div>
                <span className="text-xl font-bold text-gray-900 dark:text-white">{count}</span>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search affiliates by URL or name..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
          </div>
          {allSources.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
                className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Sources</option>
                {allSources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {allTags.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}
                className="border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <button onClick={fetchPipelineData}
            className="p-2 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
        {STAGES.map(stage => {
          const items = getFilteredItems(stage.key);
          const StageIcon = stage.icon;
          const isDragOver = dragOverStage === stage.key;

          return (
            <div key={stage.key}
              className={`flex-shrink-0 w-72 flex flex-col rounded-xl border ${stage.borderColor} ${
                isDragOver ? 'ring-2 ring-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'bg-white dark:bg-slate-900'
              } transition-all duration-200`}
              onDragOver={(e) => handleDragOver(e, stage.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.key)}>
              {/* Column Header */}
              <div className={`${stage.headerBg} p-3 rounded-t-xl flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <StageIcon className="w-4 h-4 text-white" />
                  <span className="text-sm font-semibold text-white">{stage.label}</span>
                </div>
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">{items.length}</span>
              </div>

              {/* Cards */}
              <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[60vh]">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-xs">No affiliates in this stage</div>
                ) : (
                  items.map(item => {
                    const isHigh = item.priority === 'high';
                    const isLow = item.priority === 'low';
                    const followUp = getFollowUpStatus(item.follow_up_date);
                    const tags = item.tags || [];

                    return (
                      <div key={item.id} draggable
                        onDragStart={(e) => handleDragStart(e, item, stage.key)}
                        onDragEnd={handleDragEnd}
                        onClick={() => openDetailPanel(item)}
                        className={`p-3 bg-white dark:bg-slate-800 rounded-lg cursor-pointer hover:shadow-md transition-all duration-200 group ${
                          draggedItem?.id === item.id ? 'opacity-50' : ''
                        } ${isHigh ? 'border-2 border-blue-400 dark:border-blue-600' : isLow ? 'border border-gray-200 dark:border-slate-700 opacity-75' : 'border border-gray-100 dark:border-slate-700'}`}>
                        {/* Top row: grip + source + priority star + follow-up dot */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1">
                            <GripVertical className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                            {isHigh && <Star className="w-3.5 h-3.5 text-blue-500 fill-blue-500" />}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {followUp && (
                              <div className={`w-2.5 h-2.5 rounded-full ${followUpDotColor[followUp]}`} title={`Follow-up: ${item.follow_up_date}`} />
                            )}
                            {item.source && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">{item.source}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mb-1">
                          <Globe className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.affiliate_url}</span>
                        </div>

                        {item.affiliate_name && (
                          <p className="text-xs text-gray-500 dark:text-slate-400 mb-1 pl-5 truncate">{item.affiliate_name}</p>
                        )}

                        {/* Tag chips */}
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5 mb-1">
                            {tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium">{tag}</span>
                            ))}
                            {tags.length > 3 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 font-medium">+{tags.length - 3}</span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50 dark:border-slate-700">
                          <div className="flex items-center gap-1 text-gray-400 dark:text-slate-500">
                            <Clock className="w-3 h-3" />
                            <span className="text-[10px]">{formatDate(item.updated_at)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {item.commission_type && <DollarSign className="w-3 h-3 text-blue-500" />}
                            {item.email && <Mail className="w-3 h-3 text-blue-400" />}
                            <button
                              onClick={(e) => handleContactClick(e, item)}
                              title="Compose and send email"
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold transition-colors"
                            >
                              <Send className="w-3 h-3" /> Contact
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Panel (Slide-out with Tabs) */}
      {showDetailPanel && selectedCard && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowDetailPanel(false)} />
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 shadow-2xl flex flex-col" style={{ animation: 'slideInRight 0.3s ease-out' }}>
            {/* Panel Header */}
            <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 p-4 z-10">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Affiliate Details</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDeleteItem(selectedCard.id)}
                    className="p-2 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setShowDetailPanel(false)}
                    className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-500" />
                <a href={`https://${selectedCard.affiliate_url}`} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                  {selectedCard.affiliate_url} <ExternalLink className="w-3 h-3" />
                </a>
                {selectedCard.priority === 'high' && <Star className="w-4 h-4 text-blue-500 fill-blue-500" />}
              </div>
              {selectedCard.affiliate_name && (
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{selectedCard.affiliate_name}</p>
              )}
              {selectedCard.email && (
                <div className="flex items-center gap-1 mt-1 text-sm text-gray-500 dark:text-slate-400">
                  <Mail className="w-3 h-3" /> {selectedCard.email}
                </div>
              )}

              {/* Tabs */}
              <div className="flex mt-3 -mb-4 border-b-0">
                {[
                  { key: 'overview', label: 'Overview', icon: FileText },
                  { key: 'notes', label: 'Notes', icon: FileText, count: notes.length },
                  { key: 'offers', label: 'Offers', icon: DollarSign, count: offers.length },
                  { key: 'contacts', label: 'Contacts', icon: Phone, count: contacts.length },
                  { key: 'activity', label: 'Activity', icon: Activity, count: activityLog.length }
                ].map(tab => {
                  const TabIcon = tab.icon;
                  return (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                        activeTab === tab.key
                          ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                      }`}>
                      <TabIcon className="w-3.5 h-3.5" />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`text-[10px] px-1.5 py-0 rounded-full ${
                          activeTab === tab.key ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                        }`}>{tab.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'notes' && renderNotesTab()}
              {activeTab === 'offers' && renderOffersTab()}
              {activeTab === 'contacts' && renderContactsTab()}
              {activeTab === 'activity' && renderActivityTab()}
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Add to Pipeline</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Affiliate URL <span className="text-blue-500">*</span>
                </label>
                <input type="text" placeholder="e.g. example.com" value={addForm.affiliate_url}
                  onChange={(e) => setAddForm(prev => ({ ...prev, affiliate_url: e.target.value }))}
                  className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Name (optional)</label>
                <input type="text" placeholder="Contact or site name" value={addForm.affiliate_name}
                  onChange={(e) => setAddForm(prev => ({ ...prev, affiliate_name: e.target.value }))}
                  className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Email (optional)</label>
                <input type="email" placeholder="contact@example.com" value={addForm.email}
                  onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                  Cancel
                </button>
                <button onClick={handleAddManual}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg">
                  Add to Pipeline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compose Email Popup — triggered by the "Contact" button on each card */}
      <EmailPopup
        isOpen={emailPopupOpen}
        onClose={closeEmailPopup}
        recipient={emailPopupRecipient || {}}
        allEmails={emailPopupAllEmails}
        socialMedia={emailPopupSocialMedia}
        onSend={handleEmailSendFromCard}
        customTitle={emailPopupCard ? `Contact ${emailPopupCard.affiliate_name || emailPopupCard.affiliate_url}` : ''}
        showTemplateSelection={true}
        allowAttachments={true}
        allowCc={true}
        readOnlyTo={false}
      />


      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default CrmPipeline;
